import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { formatChemicalDisplayName } from "../../chemicalLibrary/ChemicalLibraryAddEditDialog/ChemicalLibraryAddEditDialog.util";
import ChemicalSelectionFields from "../../ChemicalSelectionFields";
import {
  getChemicalOptionsByCategory,
  getInitialChemicalSelection,
} from "../../ChemicalSelectionFields/utils";
import { CurrencyLabelWithFlag } from "../../Currency";
import ChemicalCards from "./ChemicalCards";

import {
  DEFAULT_CHEMICAL_CURRENCY_CODE,
  DEFAULT_CHEMICAL_DENSITY_UNIT,
  EXCLUDED_CHEMICAL_LIST,
} from "../../../constants/chemicals.constants";
import { CURRENCY_CODES } from "../../../constants/currencies.constants";
import {
  ALLOWED_CHEMICAL_CATEGORIES,
  DATE_FORMATS,
} from "../../../constants/global.constants";
import {
  GALLON_TO_LITER_CONVERSION_FACTOR,
  UNITS,
} from "../../../constants/units.constant";

import { ThunderboltIcon, WaterDropIcon } from "../../../assets/icons";

import { formatDate, unitConverter } from "../../../utils/Utils";

const normalizeChemCategory = (category = "") => {
  if (!category) {
    return "";
  }

  if (category === "Base") {
    return "alkali";
  }

  return category.toLowerCase();
};

const normalizeChemDensity = (density, densityUnit) => {
  if (!density) {
    return "-";
  }
  if (densityUnit && densityUnit !== DEFAULT_CHEMICAL_DENSITY_UNIT) {
    return unitConverter(density, densityUnit, true).toFixed(2);
  }
  return density?.toFixed(2);
};

const normalizeChemicalPriceAndUnit = (bulkPrice, costType, density) => {
  const parsedPrice = parseFloat(bulkPrice);
  let normalizedPrice = parsedPrice;
  let normalizedUnit = costType;

  // since 1 g/cm³ = 1 kg/L, no need to convert densisty as we always normalize density to g/cm³
  if (costType === UNITS.liter) {
    normalizedPrice = parsedPrice / density;
    normalizedUnit = UNITS.kiloGram;
  } else if (costType === UNITS.gallon) {
    const pricePerLiter = parsedPrice / GALLON_TO_LITER_CONVERSION_FACTOR;
    normalizedPrice = pricePerLiter / density;
    normalizedUnit = UNITS.kiloGram;
  }

  return {
    price: normalizedPrice,
    unit: normalizedUnit,
  };
};

const normalizeChemical = (chem = {}, units = {}) => {
  const normalizedDensity = normalizeChemDensity(
    chem.bulkDensity,
    units.Density,
  );

  const { price, unit } = normalizeChemicalPriceAndUnit(
    chem.bulkPrice,
    chem.costType,
    normalizedDensity,
  );

  return {
    category: normalizeChemCategory(chem.chemCategory),
    chemical: chem.chemicalName ?? "",
    symbol: chem.symbol ?? "",
    display_name: formatChemicalDisplayName(
      chem.symbol,
      chem.bulkConcentration,
    ),
    bulk_concentration: chem.bulkConcentration ?? null,
    bulk_density: normalizedDensity ?? null,
    price: price ?? null, // TODO: conversion needed here for unsupported units
    unit: unit ?? "", // TODO: conversion needed here for unsupported units
  };
};

export const getWaveProVolumeUnitAndPriceScale = (waveProVolumeUnit) => {
  // water price is currency/unit; so unit price becomes 1000x.
  // 1 m3 = 1000 L and 1 kgal = 1000 gal; 1 $/L = 1000 $/m3; 1 $/gal = 1000 $/kgal
  if (waveProVolumeUnit === UNITS.liter) {
    return { normalizedVolumeUnit: UNITS.cubicMeter, priceScale: 1000 };
  }
  if (waveProVolumeUnit === UNITS.gallon) {
    // In WavePro, water price is already converted to kgal when gal is selected in units.
    // so, we normalize the volume label to kgal, but keep the numeric water price unchanged.
    return { normalizedVolumeUnit: "kgal", priceScale: 1 };
  }

  return { normalizedVolumeUnit: waveProVolumeUnit, priceScale: 1 };
};

const categoryRank = { acid: 0, alkali: 1, cip_additive: 2 };

const normalizeWaveProPlantSetupData = (config = {}) => {
  const units = config?.units || {};
  const currency = config?.currency || {};
  const utilityCosts = config?.utilityCosts || {};

  let {
    Conductivity,
    Flow,
    Pressure,
    Temperature,
    "Volume (Solution)": volume,
    Weight,
  } = units;

  const { normalizedVolumeUnit, priceScale } =
    getWaveProVolumeUnitAndPriceScale(volume);
  const normalizedPressureUnit = Pressure === UNITS.psi ? UNITS.psig : Pressure;
  const normalizedConductivityUnit =
    Conductivity === UNITS.mscsm ? UNITS.uscm : Conductivity;

  return {
    normalizedSettings: {
      flow: Flow,
      pressure: normalizedPressureUnit,
      feed_quality: normalizedConductivityUnit,
      permeate_quality: normalizedConductivityUnit,
      temperature: Temperature,
      volume: normalizedVolumeUnit,
      currency: CURRENCY_CODES[currency?.symbol],
      mass: Weight,
      conversion_rate: currency?.conversionRate,
    },
    normalizedUtilityCosts: {
      water_price: Number(utilityCosts?.rawWater) * priceScale,
      energy_price: Number(utilityCosts?.electricity),
    },
  };
};

const PlantSetupTab = ({
  config,
  chemicalData,
  setChemicalData,
  setNormalizedSettingsAndCosts,
}) => {
  const { t } = useTranslation();
  const { plantChemicalLibrary } = useSelector((state) => state);

  const projectInfo = config?.project || {};
  const units = config?.units || {};
  const currencySymbol = config?.currency?.symbol;
  const currencyCode =
    CURRENCY_CODES[config?.currency?.symbol] || DEFAULT_CHEMICAL_CURRENCY_CODE;
  const utilityCosts = config?.utilityCosts || {};
  const rawChemicalLibrary = config?.chemicals || [];
  const normalizedSettingsAndCosts = useMemo(
    () => normalizeWaveProPlantSetupData(config),
    [config],
  );
  const { normalizedSettings, normalizedUtilityCosts } =
    normalizedSettingsAndCosts;
  const normalizedVolumeUnit = normalizedSettings?.volume;
  const normalizedRawWaterCost = normalizedUtilityCosts?.water_price;
  const normalizedConductivityUnit = normalizedSettings?.feed_quality;

  const importedChemicals = useMemo(
    () => rawChemicalLibrary.map((chem) => normalizeChemical(chem, units)),
    [rawChemicalLibrary, units],
  );

  // To display chemicals,
  // we consider the imported chemicals that are not in the EXCLUDED_CHEMICAL_LIST
  // and are in the allowed categories(acid, alkali, cip_additive).
  const approvedChemicals = useMemo(
    () =>
      importedChemicals.filter(
        (chem) =>
          ALLOWED_CHEMICAL_CATEGORIES.includes(chem.category) &&
          !EXCLUDED_CHEMICAL_LIST.some(
            (excludedChem) => excludedChem.symbol === chem.symbol,
          ),
      ),
    [importedChemicals],
  );

  const orderedApprovedChemicals = useMemo(
    () =>
      [...approvedChemicals].sort((a, b) => {
        const rankDiff =
          (categoryRank[a?.category] ?? Number.POSITIVE_INFINITY) -
          (categoryRank[b?.category] ?? Number.POSITIVE_INFINITY);
        if (rankDiff) return rankDiff;
        const aConc = Number(a?.bulk_concentration);
        const bConc = Number(b?.bulk_concentration);
        return (
          (Number.isFinite(aConc) ? aConc : Number.POSITIVE_INFINITY) -
          (Number.isFinite(bConc) ? bConc : Number.POSITIVE_INFINITY)
        );
      }),
    [approvedChemicals],
  );

  const unitEntries = [
    ["Flow", units.Flow],
    ["Pressure", units.Pressure],
    ["Temperature", units.Temperature],
    ["Volume", normalizedVolumeUnit],
    ["Conductivity", normalizedConductivityUnit],
    ["Weight", units.Weight],
  ];

  const mergeChemicalLibraries = (baseChemicals, extraChemicals) => {
    if (extraChemicals.length === 0) {
      return baseChemicals;
    }

    const mergedMap = new Map();

    baseChemicals.forEach((item, index) => {
      const key = item.display_name;
      mergedMap.set(key, item);
    });

    // Additional chemicals override base chemicals when display name matches.
    extraChemicals.forEach((item, index) => {
      const key = item.display_name;
      mergedMap.set(key, item);
    });

    return Array.from(mergedMap.values());
  };

  const mergedChemicalLibraries = useMemo(
    () =>
      mergeChemicalLibraries(plantChemicalLibrary, orderedApprovedChemicals),
    [plantChemicalLibrary, orderedApprovedChemicals],
  );

  const chemicalOptionsByCategoryWithImportedChemicals = useMemo(() => {
    return getChemicalOptionsByCategory(mergedChemicalLibraries);
  }, [mergedChemicalLibraries]);

  const chemicalSelectionWithImportedChemicals = useMemo(
    () => getInitialChemicalSelection(mergedChemicalLibraries),
    [mergedChemicalLibraries],
  );

  // handle chemical selection changes with new imported chemicals
  const handleChemicalChange = (key, option) => {
    setChemicalData((prev) => {
      const updatedChemicals = { ...prev.chemicalSelection };
      const chemId = mergedChemicalLibraries.find(
        (chem) => chem.display_name === option.value,
      )?.chem_id;
      updatedChemicals[key] = {
        chem_id: chemId,
        chemical_name: option.label,
        price: option.price,
      };
      return {
        ...prev,
        chemicalSelection: updatedChemicals,
      };
    });
  };

  // new imported chemicals
  useEffect(() => {
    const validNewChemicals = importedChemicals.filter(
      (chem) =>
        !EXCLUDED_CHEMICAL_LIST.some(
          (excludedChem) => excludedChem.symbol === chem.symbol,
        ),
    );

    setChemicalData((prev) => ({
      ...prev,
      chemicals: validNewChemicals,
    }));
  }, [importedChemicals]);

  // set the chemical selection after we have merged import chemicals
  useEffect(() => {
    setChemicalData((prev) => ({
      ...prev,
      chemicalSelection: chemicalSelectionWithImportedChemicals,
    }));
  }, [chemicalSelectionWithImportedChemicals]);

  useEffect(() => {
    if (!setNormalizedSettingsAndCosts) {
      return;
    }

    setNormalizedSettingsAndCosts(normalizedSettingsAndCosts);
  }, [normalizedSettingsAndCosts]);

  return (
    <>
      <section className="wavepro-preview-section">
        <h4 className="wavepro-section-title">
          {t("plantInformationStrings.plantInfo")}
        </h4>
        <div className="wavepro-info-grid">
          <div>
            <span>{t("addNewPlant.installationName")}:</span>{" "}
            {projectInfo.name || "-"}
          </div>
          <div>
            <span>{t("ROOperationAdvisorTableStrings.createdon")}:</span>{" "}
            {formatDate(
              projectInfo.createDate,
              DATE_FORMATS.YYYY_MM_DD_HH_MM_SS,
            ) || "-"}
          </div>
          <div>
            <span>{t("ROOperationAdvisorTableStrings.modifiedAt")}:</span>{" "}
            {formatDate(
              projectInfo.revisionDate,
              DATE_FORMATS.YYYY_MM_DD_HH_MM_SS,
            ) || "-"}
          </div>
          <div>
            <span>{t("adminPage.company")}:</span> {projectInfo.company || "-"}
          </div>
          <div>
            <span>{t("addNewPlant.country")}:</span>{" "}
            {projectInfo.country || "-"}
          </div>
          <div>
            <span>{t("importPlantWavePro.customer")}:</span>{" "}
            {projectInfo.customer || "-"}
          </div>
        </div>
        <div className="wavepro-info-description">
          <span>{t("addNewPlant.description")}:</span>{" "}
          {projectInfo.description || "-"}
        </div>
      </section>

      <section className="wavepro-preview-section">
        <h4 className="wavepro-section-title">
          {t("importPlantWavePro.units")}
        </h4>
        <div className="wavepro-units-grid">
          {unitEntries.map(([label, value]) => (
            <div key={label} className="wavepro-unit-item">
              <div className="wavepro-unit-label">
                {t(`importPlantWavePro.${label.toLowerCase()}`)}
              </div>
              <div className="wavepro-unit-value">{value || "-"}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="wavepro-preview-section">
        <h4 className="wavepro-section-title">
          {t("settingStrings.currency")}
        </h4>
        <div className="wavepro-currency-value">
          <CurrencyLabelWithFlag currencyCode={currencyCode} />
        </div>
      </section>

      <section className="wavepro-preview-section">
        <h4 className="wavepro-section-title">
          {t("costInformationStrings.utilityCosts")}
        </h4>
        <div className="wavepro-utility-grid">
          <div>
            <WaterDropIcon className="config-icon" />
            {t("utilityCosts.priceOfWater")}:{" "}
            {utilityCosts.rawWater
              ? `${normalizedRawWaterCost} ${currencySymbol}/${normalizedVolumeUnit}`
              : "-"}
          </div>
          <div>
            <ThunderboltIcon className="config-icon" />
            {t("utilityCosts.costOfElectricity")}:{" "}
            {utilityCosts.electricity
              ? `${utilityCosts.electricity} ${currencySymbol}/${UNITS.kiloWattHour}`
              : "-"}
          </div>
        </div>
      </section>

      <section className="wavepro-preview-section">
        <h4 className="wavepro-section-title">
          {t("importPlantWavePro.chemicalLibrary")}
        </h4>
        {orderedApprovedChemicals.length > 0 ? (
          <>
            <ChemicalCards chemicals={orderedApprovedChemicals} />
            <ChemicalSelectionFields
              showPriceField={false}
              containerClassName="wavepro-selection-grid"
              additionalChemicals={orderedApprovedChemicals}
              onChemicalChange={handleChemicalChange}
              chemicalOptionsOverride={
                chemicalOptionsByCategoryWithImportedChemicals
              }
              selectedChemicals={chemicalData?.chemicalSelection || {}}
            />
          </>
        ) : (
          <span>{t("importPlantWavePro.noApprovedChemicalsMessage")}</span>
        )}
      </section>
    </>
  );
};

export default PlantSetupTab;
